import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from '@tanstack/react-router';
import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { Input } from '@heyroot/ui/primitives/Input';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import {
  fetchStaffMember,
  fetchAdminRoles,
  setStaffRole,
  setStaffStatus,
  deleteStaff,
  queryKeys,
  AdminApiError,
} from '../lib/api';
import { useAdminAuth } from '../lib/auth-context';
import { PERMS } from '../../rbac/permissions';
import { TypedConfirm } from '../components/typed-confirm';

const ERROR_COPY: Record<string, string> = {
  LAST_SUPER_ADMIN:
    'This is the last operator who can administer staff and roles. Grant those permissions to someone else first.',
  CANNOT_MODIFY_SELF:
    'You cannot change your own account here. Ask another operator with staff permissions.',
  UNKNOWN_ROLE: 'That role no longer exists. Reload the page and pick again.',
  STAFF_NOT_FOUND: 'This operator no longer exists.',
  INVALID_BODY: 'That request was not valid. Reload the page and try again.',
};

function copyFor(err: unknown, fallback: string): string {
  if (err instanceof AdminApiError) return ERROR_COPY[err.code] ?? err.message;
  return fallback;
}

function statusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'active':
      return 'success';
    case 'suspended':
      return 'warning';
    case 'deleted':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function StaffDetailPage() {
  const { uid } = useParams({ strict: false }) as { uid: string };
  const { hasPerm } = useAdminAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canManage = hasPerm(PERMS.STAFF_MANAGE);

  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [roleSaved, setRoleSaved] = useState(false);

  const {
    data: staff,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.staffMember(uid),
    queryFn: () => fetchStaffMember(uid),
  });
  const { data: roles } = useQuery({
    queryKey: queryKeys.adminRoles,
    queryFn: fetchAdminRoles,
  });

  const roleMutation = useMutation({
    mutationFn: (roleId: string) => setStaffRole(uid, roleId),
    onSuccess: () => {
      setError(null);
      setRoleSaved(true);
      void qc.invalidateQueries({ queryKey: queryKeys.staffMember(uid) });
    },
    onError: (err: unknown) => setError(copyFor(err, 'Could not change the role.')),
  });

  const statusMutation = useMutation({
    // C2 fix (whole-branch review): omit `reason` entirely when empty --
    // sending `reason: ''` makes it PRESENT on the wire, which the server's
    // `.trim().min(1).optional()` schema rejects with 400 (an empty string
    // does not satisfy .optional()). Reactivate never collects a reason.
    mutationFn: (status: 'active' | 'suspended') => {
      const r = reason.trim();
      return setStaffStatus(uid, { status, ...(r ? { reason: r } : {}) });
    },
    onSuccess: () => {
      setError(null);
      setConfirmSuspend(false);
      setReason('');
      void qc.invalidateQueries({ queryKey: queryKeys.staffMember(uid) });
    },
    onError: (err: unknown) => setError(copyFor(err, 'Could not update this operator.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (r: string) => deleteStaff(uid, r),
    // /harden review pass, 2026-08-30: closing the dialog was the only effect,
    // so after a successful delete the page kept rendering the operator as
    // active with Suspend/Delete live. The next Suspend then 404s (getStaff
    // filters deleted_at IS NULL) and reads as a broken delete rather than a
    // completed one. Mirrors role-detail.tsx's deleteMutation: drop the cached
    // rows, then leave the detail route for the list. The list key is
    // parameterized, so invalidate on the ['staff'] prefix to catch every
    // filter/page combination rather than one exact param set.
    onSuccess: () => {
      setError(null);
      setConfirmDelete(false);
      void qc.invalidateQueries({ queryKey: ['staff'] });
      void navigate({ to: '/staff' });
    },
    onError: (err: unknown) => setError(copyFor(err, 'Could not delete this operator.')),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !staff) {
    return (
      <EmptyState title="Operator not found" description="This operator may have been removed." />
    );
  }

  const isSuspended = staff.status === 'suspended';

  return (
    <div className="flex flex-col gap-6 max-w-[900px]">
      <div>
        <Link to="/staff" className="text-body-sm text-secondary hover:text-primary">
          Staff
        </Link>
      </div>

      <div className="flex items-center gap-2.5">
        <h1 className="text-heading-2 text-primary">{staff.displayName ?? staff.email}</h1>
        <Badge variant={statusVariant(staff.status)}>{staff.status}</Badge>
      </div>

      <section className="rounded-control border border-border bg-surface-elevated p-5 flex flex-col gap-3">
        <h2 className="text-eyebrow uppercase text-muted">Identity</h2>
        <dl
          className="grid gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}
        >
          <div>
            <dt className="text-eyebrow uppercase text-muted">Email</dt>
            <dd className="text-body-sm text-primary">{staff.email}</dd>
          </div>
          <div>
            <dt className="text-eyebrow uppercase text-muted">Last login</dt>
            <dd className="text-body-sm text-primary">
              {staff.lastLoginAt ? new Date(staff.lastLoginAt).toLocaleDateString() : '—'}
            </dd>
          </div>
          {isSuspended && staff.suspendReason && (
            <div>
              <dt className="text-eyebrow uppercase text-muted">Suspend reason</dt>
              <dd className="text-body-sm text-primary">{staff.suspendReason}</dd>
            </div>
          )}
        </dl>

        {canManage && (
          <div className="w-56">
            <Select
              value={staff.role ?? ''}
              onValueChange={async (v) => {
                setError(null);
                setRoleSaved(false);
                try {
                  await roleMutation.mutateAsync(v);
                } catch (err) {
                  setError(copyFor(err, 'Could not change the role.'));
                }
              }}
            >
              <SelectTrigger aria-label="Role" disabled={roleMutation.isPending}>
                <SelectValue placeholder="No role" />
              </SelectTrigger>
              <SelectContent>
                {(roles ?? []).map((r) => (
                  <SelectItem key={r.roleId} value={r.roleId}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {roleSaved && !error && (
              <p className="mt-1 text-body-sm text-success-text">Role updated.</p>
            )}
          </div>
        )}
      </section>

      {error && <p className="text-body-sm text-danger-text">{error}</p>}

      {canManage && (
        <section className="rounded-control border border-border bg-surface-elevated p-5 flex flex-col gap-3">
          <h2 className="text-eyebrow uppercase text-muted">Actions</h2>
          <div className="flex items-center gap-3">
            {!isSuspended ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setError(null);
                  setConfirmSuspend(true);
                }}
              >
                Suspend operator
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={async () => {
                  try {
                    await statusMutation.mutateAsync('active');
                  } catch (err) {
                    setError(copyFor(err, 'Could not update this operator.'));
                  }
                }}
                disabled={statusMutation.isPending}
              >
                Reactivate operator
              </Button>
            )}
            {/* Delete operator is a text-danger action, not a solid red button at
                equal visual weight with the role save (Slice 2 follow-up 6). */}
            <Button
              variant="link"
              className="text-body-sm text-danger-text"
              onClick={() => {
                setError(null);
                setConfirmDelete(true);
              }}
            >
              Delete operator
            </Button>
          </div>

          {confirmSuspend && (
            <div
              role="alertdialog"
              aria-label="Confirm suspend operator"
              className="flex flex-col gap-3 rounded-control border border-danger bg-danger-pale p-4"
            >
              <p className="text-body-sm text-danger-text">
                Suspending {staff.email} revokes their admin access immediately.
              </p>
              <label className="flex flex-col gap-1 text-body-sm text-secondary">
                Reason (recorded in the audit log)
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  aria-label="Reason"
                />
              </label>
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  disabled={reason.trim().length === 0 || statusMutation.isPending}
                  onClick={async () => {
                    try {
                      await statusMutation.mutateAsync('suspended');
                    } catch (err) {
                      setError(copyFor(err, 'Could not update this operator.'));
                    }
                  }}
                >
                  Suspend
                </Button>
                <Button variant="ghost" onClick={() => setConfirmSuspend(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <TypedConfirm
            open={confirmDelete}
            title="Delete operator"
            body={`This permanently removes ${staff.email}'s admin access.`}
            confirmValue={staff.email}
            confirmLabel="Delete"
            requireReason
            pending={deleteMutation.isPending}
            onCancel={() => setConfirmDelete(false)}
            onConfirm={async (r) => {
              try {
                await deleteMutation.mutateAsync(r);
              } catch (err) {
                setError(copyFor(err, 'Could not delete this operator.'));
              }
            }}
          />
        </section>
      )}
    </div>
  );
}
